import { useEffect, useRef, useState } from "react";
import { LocateFixed, Minus, Plus, Search } from "lucide-react";
import { LocalAeqdProjection } from "@map2threejs/core/projection";
import type { LatLng, RegionSelection } from "@map2threejs/core/types";
import { loadGoogleMaps } from "../google-maps";

interface SelectorProps {
  apiKey: string;
  center: LatLng;
  radiusMeters: number;
  selectionMode: RegionSelection["mode"];
  polygonVertices: LatLng[];
  drawingPolygon: boolean;
  onCircleChange(center: LatLng, radiusMeters: number): void;
  onPolygonChange(vertices: LatLng[]): void;
  onSearchCenter(center: LatLng): void;
  onFinishDrawing(): void;
}

interface DemoLocationResult {
  id: string;
  label: string;
  center: LatLng;
}

interface GooglePlaceSuggestion {
  id: string;
  label: string;
  detail?: string;
  prediction: google.maps.places.PlacePrediction;
}

function googlePlacesError(reason: unknown): string {
  const message = reason instanceof Error ? reason.message : String(reason);
  if (/API_KEY_SERVICE_BLOCKED|Rpc failed due to xhr error|PLACES_SEARCH_TEXT:\s*UNKNOWN/i.test(message)) {
    return "Google Places API (New) is blocked for this browser key. Add Places API (New) to this key's API restrictions.";
  }
  if (/REQUEST_DENIED|PERMISSION_DENIED|API key|not activated|not authorized/i.test(message)) {
    return "Google Places API (New) rejected this request. Check that it is enabled and allowed by this browser key.";
  }
  return message || "Google Places search failed.";
}

function placeLabel(place: google.maps.places.Place): string {
  return place.formattedAddress?.trim() || place.displayName?.trim() || "Selected place";
}

function DemoLocationSearch({
  onSearch,
  onSelect,
}: {
  onSearch(query: string): Promise<DemoLocationResult>;
  onSelect(result: DemoLocationResult): void;
}) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [searching, setSearching] = useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const value = query.trim();
    if (!value || searching) return;
    setSearching(true);
    try {
      const result = await onSearch(value);
      setQuery(result.label);
      setStatus(result.label);
      onSelect(result);
    } catch (reason) {
      setStatus(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setSearching(false);
    }
  };
  return (
    <form className="location-search" role="search" onSubmit={(event) => { void submit(event); }}>
      <Search size={19} aria-hidden="true" />
      <input
        aria-label="Search for a location"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setStatus(undefined);
        }}
        placeholder="Search city, address or landmark"
      />
      <button type="submit" disabled={!query.trim() || searching}>{searching ? "Searching…" : "Search"}</button>
      {status && <span className="location-search-status" role="status">{status}</span>}
    </form>
  );
}

function GooglePlacesSearch({
  ready,
  loadError,
  map,
  onSelect,
}: {
  ready: boolean;
  loadError: string | undefined;
  map: google.maps.Map | undefined;
  onSelect(place: google.maps.places.Place): void;
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<GooglePlaceSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [editing, setEditing] = useState(false);
  const onSelectRef = useRef(onSelect);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [searching, setSearching] = useState(false);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!ready || !editing || !map || query.trim().length < 2) {
      setSuggestions([]);
      setActiveIndex(-1);
      return;
    }
    let disposed = false;
    const timer = globalThis.setTimeout(() => {
      const request: google.maps.places.AutocompleteRequest = {
        input: query.trim(),
        sessionToken: sessionTokenRef.current ??= new google.maps.places.AutocompleteSessionToken(),
      };
      const locationBias = map.getBounds() ?? map.getCenter();
      if (locationBias) request.locationBias = locationBias;
      void google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
        .then(({ suggestions: nextSuggestions }) => {
          if (disposed) return;
          const places = nextSuggestions
            .map((suggestion): GooglePlaceSuggestion | undefined => {
              const prediction = suggestion.placePrediction;
              if (!prediction) return undefined;
              return {
                id: prediction.placeId,
                label: prediction.mainText?.text || prediction.text.text,
                ...(prediction.secondaryText?.text ? { detail: prediction.secondaryText.text } : {}),
                prediction,
              };
            })
            .filter((suggestion): suggestion is GooglePlaceSuggestion => Boolean(suggestion));
          setSuggestions(places);
          setActiveIndex(-1);
          setStatus(undefined);
        })
        .catch((reason: unknown) => {
          if (disposed) return;
          setSuggestions([]);
          setStatus(googlePlacesError(reason));
        });
    }, 250);
    return () => {
      disposed = true;
      globalThis.clearTimeout(timer);
    };
  }, [editing, map, query, ready]);

  const choose = async (suggestion: GooglePlaceSuggestion): Promise<void> => {
    setEditing(false);
    setSuggestions([]);
    setActiveIndex(-1);
    setSearching(true);
    setQuery(suggestion.label);
    try {
      const place = suggestion.prediction.toPlace();
      await place.fetchFields({
        fields: ["displayName", "formattedAddress", "location", "viewport"],
      });
      if (!place.location) throw new Error("This Google place has no map location.");
      const label = placeLabel(place);
      setQuery(label);
      setStatus(label);
      sessionTokenRef.current = undefined;
      onSelectRef.current(place);
    } catch (reason) {
      setStatus(googlePlacesError(reason));
    } finally {
      setSearching(false);
    }
  };

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const normalizedQuery = query.trim();
    if (!normalizedQuery || !map || searching) return;
    const suggestedPlace = activeIndex >= 0 ? suggestions[activeIndex] : suggestions[0];
    if (suggestedPlace) {
      await choose(suggestedPlace);
      return;
    }
    setSearching(true);
    try {
      const request: google.maps.places.SearchByTextRequest = {
        textQuery: normalizedQuery,
        fields: ["displayName", "formattedAddress", "location", "viewport"],
        maxResultCount: 1,
      };
      const locationBias = map.getBounds() ?? map.getCenter();
      if (locationBias) request.locationBias = locationBias;
      const { places } = await google.maps.places.Place.searchByText(request);
      const place = places[0];
      if (!place?.location) throw new Error("No matching Google place was found.");
      const label = placeLabel(place);
      setEditing(false);
      setSuggestions([]);
      setQuery(label);
      setStatus(label);
      sessionTokenRef.current = undefined;
      onSelectRef.current(place);
    } catch (reason) {
      setStatus(googlePlacesError(reason));
    } finally {
      setSearching(false);
    }
  };

  return (
    <form className="location-search" role="search" onSubmit={(event) => { void submit(event); }}>
      <Search size={19} aria-hidden="true" />
      <input
        aria-label="Search for a location"
        role="combobox"
        aria-autocomplete="list"
        aria-controls="google-place-suggestions"
        aria-expanded={suggestions.length > 0}
        aria-activedescendant={activeIndex >= 0 ? `google-place-suggestion-${activeIndex}` : undefined}
        value={query}
        onFocus={() => setEditing(true)}
        onChange={(event) => {
          setEditing(true);
          setQuery(event.target.value);
          setStatus(undefined);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && suggestions.length > 0) {
            event.preventDefault();
            setActiveIndex((current) => Math.min(suggestions.length - 1, current + 1));
          } else if (event.key === "ArrowUp" && suggestions.length > 0) {
            event.preventDefault();
            setActiveIndex((current) => Math.max(0, current - 1));
          } else if (event.key === "Escape") {
            setSuggestions([]);
            setActiveIndex(-1);
          }
        }}
        placeholder="Search city, address or landmark"
      />
      <button type="submit" disabled={!ready || !query.trim() || searching}>{searching ? "Searching…" : "Search"}</button>
      {suggestions.length > 0 && (
        <div className="location-search-suggestions" id="google-place-suggestions" role="listbox" aria-label="Google place suggestions">
          {suggestions.map((suggestion, index) => (
            <button
              id={`google-place-suggestion-${index}`}
              key={suggestion.id}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              className={index === activeIndex ? "active" : ""}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => { void choose(suggestion); }}
            >
              <Search size={15} aria-hidden="true" />
              <span><strong>{suggestion.label}</strong>{suggestion.detail && <small>{suggestion.detail}</small>}</span>
            </button>
          ))}
          <div className="google-search-attribution">Google</div>
        </div>
      )}
      {(loadError || status) && <span className="location-search-status" role={loadError ? "alert" : "status"}>{loadError ?? status}</span>}
    </form>
  );
}

export function GoogleSelector({
  apiKey,
  center,
  radiusMeters,
  selectionMode,
  polygonVertices,
  drawingPolygon,
  onCircleChange,
  onPolygonChange,
  onSearchCenter,
  onFinishDrawing,
}: SelectorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | undefined>(undefined);
  const circleRef = useRef<google.maps.Circle | undefined>(undefined);
  const polygonRef = useRef<google.maps.Polygon | undefined>(undefined);
  const polygonGuideRef = useRef<google.maps.Polyline | undefined>(undefined);
  const polygonPathRef = useRef<google.maps.MVCArray<google.maps.LatLng> | undefined>(undefined);
  const polygonVertexMarkersRef = useRef<google.maps.Circle[]>([]);
  const syncingRef = useRef(false);
  const latestRef = useRef({
    selectionMode,
    drawingPolygon,
    polygonVertices,
    onCircleChange,
    onPolygonChange,
    onFinishDrawing,
  });
  const [mapType, setMapType] = useState<"satellite" | "roadmap">("satellite");
  const [mapReady, setMapReady] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [placesReady, setPlacesReady] = useState(false);
  const [placesError, setPlacesError] = useState<string | undefined>(undefined);
  latestRef.current = {
    selectionMode,
    drawingPolygon,
    polygonVertices,
    onCircleChange,
    onPolygonChange,
    onFinishDrawing,
  };

  useEffect(() => {
    let disposed = false;
    setMapReady(false);
    setPlacesReady(false);
    setPlacesError(undefined);
    void loadGoogleMaps(apiKey)
      .then(async (googleApi) => {
        if (disposed || !containerRef.current) return;
        const map = new googleApi.maps.Map(containerRef.current, {
          center,
          zoom: 16,
          mapTypeId: mapType,
          disableDefaultUI: true,
          clickableIcons: false,
          keyboardShortcuts: true,
          gestureHandling: "greedy",
          tilt: 0,
          disableDoubleClickZoom: true,
        });
        const circle = new googleApi.maps.Circle({
          map,
          center,
          radius: radiusMeters,
          draggable: true,
          editable: true,
          strokeColor: "#68f6c3",
          strokeOpacity: 1,
          strokeWeight: 3,
          fillColor: "#2cc38a",
          fillOpacity: 0.2,
        });
        const polygonPath = new googleApi.maps.MVCArray(
          polygonVertices.map((point) => new googleApi.maps.LatLng(point)),
        );
        const polygonPaths = new googleApi.maps.MVCArray([polygonPath]);
        const polygon = new googleApi.maps.Polygon({
          map: null,
          paths: polygonPaths,
          draggable: false,
          editable: false,
          clickable: true,
          strokeColor: "#68f6c3",
          strokeOpacity: 1,
          strokeWeight: 3,
          fillColor: "#2cc38a",
          fillOpacity: 0.2,
          zIndex: 2,
        });
        const polygonGuide = new googleApi.maps.Polyline({
          map: null,
          path: [],
          clickable: false,
          strokeColor: "#68f6c3",
          strokeOpacity: 1,
          strokeWeight: 3,
          zIndex: 3,
        });
        mapRef.current = map;
        circleRef.current = circle;
        polygonRef.current = polygon;
        polygonGuideRef.current = polygonGuide;
        polygonPathRef.current = polygonPath;
        setMapReady(true);
        try {
          await googleApi.maps.importLibrary("places");
          if (!disposed) setPlacesReady(true);
        } catch (reason) {
          if (!disposed) setPlacesError(googlePlacesError(reason));
        }
        const publishCircle = (): void => {
          if (syncingRef.current || latestRef.current.selectionMode !== "circle") return;
          const nextCenter = circle.getCenter();
          if (!nextCenter) return;
          latestRef.current.onCircleChange(
            { lat: nextCenter.lat(), lng: nextCenter.lng() },
            Math.max(50, Math.min(1000, circle.getRadius())),
          );
        };
        const publishPolygon = (): void => {
          if (syncingRef.current || latestRef.current.selectionMode !== "polygon") return;
          latestRef.current.onPolygonChange(
            polygonPath.getArray().map((point) => ({ lat: point.lat(), lng: point.lng() })),
          );
        };
        circle.addListener("center_changed", publishCircle);
        circle.addListener("radius_changed", publishCircle);
        googleApi.maps.event.addListener(polygonPath, "insert_at", publishPolygon);
        googleApi.maps.event.addListener(polygonPath, "set_at", publishPolygon);
        googleApi.maps.event.addListener(polygonPath, "remove_at", publishPolygon);
        map.addListener("click", (event: google.maps.MapMouseEvent) => {
          if (
            latestRef.current.selectionMode !== "polygon"
            || !latestRef.current.drawingPolygon
            || !event.latLng
          ) return;
          latestRef.current.onPolygonChange([
            ...latestRef.current.polygonVertices,
            { lat: event.latLng.lat(), lng: event.latLng.lng() },
          ]);
        });
        map.addListener("dblclick", () => {
          if (latestRef.current.selectionMode === "polygon" && latestRef.current.polygonVertices.length >= 3) {
            latestRef.current.onFinishDrawing();
          }
        });
        const bounds = circle.getBounds();
        if (bounds) map.fitBounds(bounds, 74);
      })
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : String(reason)));
    return () => {
      disposed = true;
      if (circleRef.current) google.maps.event.clearInstanceListeners(circleRef.current);
      if (polygonRef.current) google.maps.event.clearInstanceListeners(polygonRef.current);
      if (polygonGuideRef.current) google.maps.event.clearInstanceListeners(polygonGuideRef.current);
      if (polygonPathRef.current) google.maps.event.clearInstanceListeners(polygonPathRef.current);
      if (mapRef.current) google.maps.event.clearInstanceListeners(mapRef.current);
      for (const marker of polygonVertexMarkersRef.current) marker.setMap(null);
      circleRef.current?.setMap(null);
      polygonRef.current?.setMap(null);
      polygonGuideRef.current?.setMap(null);
      circleRef.current = undefined;
      polygonRef.current = undefined;
      polygonGuideRef.current = undefined;
      polygonPathRef.current = undefined;
      polygonVertexMarkersRef.current = [];
      mapRef.current = undefined;
    };
  }, [apiKey]);

  useEffect(() => {
    const circle = circleRef.current;
    if (!circle) return;
    circle.setMap(selectionMode === "circle" ? mapRef.current ?? null : null);
    const current = circle.getCenter();
    if (
      current
      && Math.abs(current.lat() - center.lat) < 1e-8
      && Math.abs(current.lng() - center.lng) < 1e-8
      && Math.abs(circle.getRadius() - radiusMeters) < 0.01
    ) return;
    syncingRef.current = true;
    circle.setCenter(center);
    circle.setRadius(radiusMeters);
    syncingRef.current = false;
  }, [center.lat, center.lng, mapReady, radiusMeters, selectionMode]);

  useEffect(() => {
    const polygon = polygonRef.current;
    const guide = polygonGuideRef.current;
    const path = polygonPathRef.current;
    const map = mapRef.current;
    if (!polygon || !guide || !path) return;
    const isPolygonMode = selectionMode === "polygon";
    polygon.setMap(isPolygonMode && polygonVertices.length >= 3 ? map ?? null : null);
    polygon.setOptions({ editable: isPolygonMode && polygonVertices.length >= 3 });
    guide.setMap(isPolygonMode && polygonVertices.length >= 2 ? map ?? null : null);
    guide.setPath(
      polygonVertices.length >= 3
        ? [...polygonVertices, polygonVertices[0]!]
        : polygonVertices,
    );
    const current = path.getArray();
    const pathIsCurrent = current.length === polygonVertices.length
      && current.every((point, index) => {
        const next = polygonVertices[index];
        return next && Math.abs(point.lat() - next.lat) < 1e-8 && Math.abs(point.lng() - next.lng) < 1e-8;
      });
    if (!pathIsCurrent) {
      syncingRef.current = true;
      path.clear();
      for (const point of polygonVertices) path.push(new google.maps.LatLng(point));
      syncingRef.current = false;
    }

    for (const marker of polygonVertexMarkersRef.current) marker.setMap(null);
    polygonVertexMarkersRef.current = isPolygonMode && polygonVertices.length < 3
      ? polygonVertices.map((point) => new google.maps.Circle({
          map: map ?? null,
          center: point,
          radius: 6,
          clickable: false,
          strokeColor: "#0a2328",
          strokeOpacity: 1,
          strokeWeight: 2,
          fillColor: "#68f6c3",
          fillOpacity: 1,
          zIndex: 4,
        }))
      : [];
  }, [mapReady, polygonVertices, selectionMode]);

  useEffect(() => {
    mapRef.current?.setMapTypeId(mapType);
  }, [mapReady, mapType]);

  const zoom = (delta: number): void => {
    const map = mapRef.current;
    if (map) map.setZoom((map.getZoom() ?? 16) + delta);
  };

  const selectPlace = (place: google.maps.places.Place): void => {
    const location = place.location;
    if (!location) return;
    const nextCenter = { lat: location.lat(), lng: location.lng() };
    onSearchCenter(nextCenter);
    const map = mapRef.current;
    if (place.viewport) {
      map?.fitBounds(place.viewport, 74);
    } else {
      map?.panTo(nextCenter);
    }
    globalThis.setTimeout(() => {
      map?.panTo(nextCenter);
      if ((map?.getZoom() ?? 0) < 15) map?.setZoom(16);
    }, 0);
  };

  return (
    <div className={`map-stage ${drawingPolygon ? "drawing-polygon" : ""}`} aria-label="Google Maps region selector">
      <div ref={containerRef} className="google-map" />
      <GooglePlacesSearch
        ready={placesReady}
        loadError={placesError}
        map={mapRef.current}
        onSelect={selectPlace}
      />
      {error && <div className="map-error" role="alert"><strong>Google Maps unavailable</strong><span>{error}</span></div>}
      {selectionMode === "polygon" && drawingPolygon && (
        <div className="drawing-instruction">
          Click around the desired area · double-click or use “Finish area” when done
        </div>
      )}
      <div className="map-type-control" role="group" aria-label="Map type">
        <button className={mapType === "satellite" ? "selected" : ""} onClick={() => setMapType("satellite")}>Satellite</button>
        <button className={mapType === "roadmap" ? "selected" : ""} onClick={() => setMapType("roadmap")}>Map</button>
      </div>
      <div className="map-zoom-control" aria-label="Map controls">
        <button onClick={() => zoom(1)} aria-label="Zoom in"><Plus /></button>
        <button onClick={() => zoom(-1)} aria-label="Zoom out"><Minus /></button>
        <button onClick={() => mapRef.current?.panTo(center)} aria-label="Center selected region"><LocateFixed /></button>
      </div>
      <div className="map-attribution">Selection and place search © Google</div>
    </div>
  );
}

export function DemoSelector(props: Omit<SelectorProps, "apiKey">) {
  const {
    center,
    radiusMeters,
    selectionMode,
    polygonVertices,
    drawingPolygon,
    onCircleChange,
    onPolygonChange,
    onSearchCenter,
    onFinishDrawing,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const originRef = useRef(center);
  const dragRef = useRef<{ kind: "center" | "radius"; startX: number; startY: number; center: LatLng; radius: number } | undefined>(undefined);
  const circleSize = Math.max(220, Math.min(720, 360 + radiusMeters * 1.3));
  const projection = new LocalAeqdProjection(originRef.current);
  const polygonPoints = polygonVertices.map((point) => {
    const projected = projection.forward(point);
    return `${projected.x / 1.5},${-projected.north / 1.5}`;
  }).join(" ");

  const beginDrag = (kind: "center" | "radius", event: React.PointerEvent): void => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { kind, startX: event.clientX, startY: event.clientY, center, radius: radiusMeters };
  };
  const moveDrag = (event: React.PointerEvent): void => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (drag.kind === "radius") {
      onCircleChange(center, Math.max(50, Math.min(1000, drag.radius + dx * 3)));
      return;
    }
    const metersPerDegreeLat = 111_320;
    const metersPerDegreeLng = metersPerDegreeLat * Math.cos((drag.center.lat * Math.PI) / 180);
    onCircleChange(
      {
        lat: drag.center.lat - (dy * 1.5) / metersPerDegreeLat,
        lng: drag.center.lng + (dx * 1.5) / metersPerDegreeLng,
      },
      radiusMeters,
    );
  };
  const addPolygonPoint = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (selectionMode !== "polygon" || !drawingPolygon) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - bounds.left - bounds.width / 2) * 1.5;
    const north = -(event.clientY - bounds.top - bounds.height / 2) * 1.5;
    onPolygonChange([...polygonVertices, projection.inverse(x, north)]);
  };
  const searchLocation = async (query: string): Promise<DemoLocationResult> => {
    const normalized = query.trim().toLowerCase();
    const nextCenter = normalized.includes("paris")
      ? { lat: 48.8566, lng: 2.3522 }
      : normalized.includes("london")
        ? { lat: 51.5072, lng: -0.1276 }
        : { lat: 44.4268, lng: 26.1025 };
    const label = normalized.includes("paris") ? "Paris, France" : normalized.includes("london") ? "London, UK" : "Bucharest, Romania";
    return { id: normalized || "bucharest", label, center: nextCenter };
  };

  const selectLocation = (result: DemoLocationResult): void => {
    originRef.current = result.center;
    onSearchCenter(result.center);
  };

  return (
    <div
      ref={containerRef}
      className={`map-stage demo-map ${drawingPolygon ? "drawing-polygon" : ""}`}
      onPointerMove={moveDrag}
      onPointerUp={() => { dragRef.current = undefined; }}
      onClick={addPolygonPoint}
      onDoubleClick={(event) => {
        event.preventDefault();
        if (selectionMode === "polygon" && polygonVertices.length >= 3) onFinishDrawing();
      }}
      aria-label="Demo fixture region selector"
    >
      <div className="demo-road road-one" />
      <div className="demo-road road-two" />
      <div className="demo-block-grid" />
      <DemoLocationSearch onSearch={searchLocation} onSelect={selectLocation} />
      {selectionMode === "circle" ? (
        <div className="selection-circle" style={{ width: circleSize, height: circleSize }}>
          <button className="selection-center" onPointerDown={(event) => beginDrag("center", event)} aria-label="Drag region center" />
          <span className="selection-radius-line" />
          <span className="selection-radius-label">{Math.round(radiusMeters)} m</span>
          <button className="selection-radius-handle" onPointerDown={(event) => beginDrag("radius", event)} aria-label="Drag region radius">↔</button>
        </div>
      ) : (
        <svg className="demo-polygon" viewBox="-500 -350 1000 700" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <polygon points={polygonPoints} />
          {polygonVertices.map((point, index) => {
            const projected = projection.forward(point);
            return <circle key={`${point.lat}:${point.lng}:${index}`} cx={projected.x / 1.5} cy={-projected.north / 1.5} r="7" />;
          })}
        </svg>
      )}
      {selectionMode === "polygon" && drawingPolygon && (
        <div className="drawing-instruction">Click around the desired area · double-click or use “Finish area” when done</div>
      )}
      <div className="demo-notice">Deterministic demo selector — live mode uses Google satellite</div>
      <div className="map-attribution">Demo fixture · live selection © Google</div>
    </div>
  );
}
